import React, { useState, ChangeEvent, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Image } from '../../../api/common';
import { Button } from '../../../components/Button';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/x.svg';
import { ReactComponent as ViewIcon } from '../../../assets/icons/view.svg';
import { ReactComponent as ImagePlaceholder } from '../../../assets/icons/image.svg';
import { Loader } from '../../../components/Loader';
import { ImageEditorModal } from '../../../components/ImageEditorModal';
import { ImagePreviewModal } from '../../../components/ImagePreviewModal';

interface SortableImageProps {
  image: Image;
  onDelete: (id: string) => void;
  onPreview: (image: Image) => void;
}

const SortableImage: React.FC<SortableImageProps> = ({
  image,
  onDelete,
  onPreview,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
      {...attributes}
      {...listeners}
    >
      <img
        src={image.url}
        alt={image.title}
        className="object-cover w-24 h-24 rounded-md border border-gray-200"
        draggable={false}
      />
      <Button
        variant="icon"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(image.id);
        }}
        className="absolute -top-2 -right-2 bg-white shadow-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <DeleteIcon className="w-3 h-3" />
      </Button>
      <Button
        variant="icon"
        onClick={(e) => {
          e.stopPropagation();
          onPreview(image);
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/90 shadow-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ViewIcon className="w-4 h-4" />
      </Button>
      <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
        Drag
      </div>
    </div>
  );
};

interface SortableImageGalleryProps {
  images: Image[];
  onChange: (images: Image[]) => void;
  onAddImage: (file: File) => Promise<void>;
  onRequestDelete?: (image: Image) => void;
  label?: React.ReactNode;
  maxImages?: number;
  aspectRatio?: number;
}

export const SortableImageGallery: React.FC<SortableImageGalleryProps> = ({
  images,
  onChange,
  onAddImage,
  onRequestDelete,
  label,
  maxImages = 10,
  aspectRatio,
}) => {
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<Image | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      onChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleDelete = (id: string) => {
    const image = images.find((img) => img.id === id);
    if (onRequestDelete && image) {
      onRequestDelete(image);
    } else {
      onChange(images.filter((img) => img.id !== id));
    }
  };

  const handlePreview = (image: Image) => {
    setPreviewImage(image);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewImage(null);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = ''; // Reset input

    if (aspectRatio) {
      // Open editor when aspectRatio is provided
      setSelectedFile(file);
      setEditorOpen(true);
    } else {
      // Original flow without editor
      setLoading(true);
      try {
        await onAddImage(file);
      } catch (error) {
        console.error('Error uploading image:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditorConfirm = async (croppedFile: File) => {
    setEditorOpen(false);
    setSelectedFile(null);
    setLoading(true);
    try {
      await onAddImage(croppedFile);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setSelectedFile(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const processFile = (file: File) => {
    if (aspectRatio) {
      setSelectedFile(file);
      setEditorOpen(true);
    } else {
      setLoading(true);
      onAddImage(file)
        .catch((error) => console.error('Error uploading image:', error))
        .finally(() => setLoading(false));
    }
  };

  const handleAddTileClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      {label && <span className="text-sm mb-0.5 block">{label}</span>}
      <div className="border border-dashed rounded-2xl p-4 border-gray-300 min-h-[10.5rem]">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader />
          </div>
        ) : images.length === 0 ? (
          /* Empty state - dropzone-style UI */
          <div
            className={`flex flex-col items-center justify-center h-32 cursor-pointer transition-colors ${
              isDragOver ? 'bg-gray-100' : ''
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleAddTileClick}
          >
            <ImagePlaceholder className="w-10 h-10 text-gray-400 mb-2" />
            <p className="text-sm text-black/90">
              Drag and Drop file here or{' '}
              <span className="text-primary-500 underline">Choose file</span>
            </p>
            <p className="text-black/60 text-sm mt-1">PNG, JPG (Max 5MB)</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept="image/*"
            />
          </div>
        ) : (
          /* Has images - show gallery with add tile */
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={images.map((img) => img.id)}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-4">
                  {images.map((image) => (
                    <SortableImage
                      key={image.id}
                      image={image}
                      onDelete={handleDelete}
                      onPreview={handlePreview}
                    />
                  ))}

                  {/* Add image tile */}
                  {images.length < maxImages && (
                    <div
                      className={`w-24 h-24 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors ${
                        isDragOver ? 'border-primary bg-gray-100' : ''
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={handleAddTileClick}
                    >
                      <ImagePlaceholder className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-primary underline">Choose file</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>

      {/* Image Editor Modal */}
      {aspectRatio && (
        <ImageEditorModal
          isOpen={editorOpen}
          onClose={handleEditorClose}
          onConfirm={handleEditorConfirm}
          imageFile={selectedFile}
          aspectRatio={aspectRatio}
        />
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={previewOpen}
        onClose={handlePreviewClose}
        image={previewImage}
      />
    </div>
  );
};
